import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ChevronLeft, ChevronRight, MapPin, DollarSign, Users as UsersIcon } from "lucide-react";
import { pipelineTechApi, usersApi } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * Mobile Pipeline — non-technician view.
 *
 * Reuses the same read-only pipelineTechApi endpoints that admins already
 * have full visibility over (the backend does not filter cards for
 * admin/dispatcher callers). Adds an "Owner" filter so admins can preview
 * the board from any team member's perspective without leaving mobile.
 */
export default function MobilePipeline() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = user && (user.role === "admin" || user.role === "dispatcher" || user.role === "super_admin");

  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState("");
  const [board, setBoard] = useState({ stages: [], cards: [] });
  const [members, setMembers] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState("all"); // "all" | "unassigned" | userId
  const [loading, setLoading] = useState(true);
  const [boardLoading, setBoardLoading] = useState(false);
  const scrollerRef = useRef(null);
  const [activeStageIdx, setActiveStageIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    pipelineTechApi.pipelines()
      .then((list) => {
        if (cancelled) return;
        setPipelines(list || []);
        if ((list || []).length && !pipelineId) setPipelineId(list[0].id);
      })
      .catch(() => setPipelines([]))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Load team members (used to populate the Owner filter). Admins see the
  // full list; other managers see everyone too since they can dispatch.
  useEffect(() => {
    if (!isManager) return;
    let cancelled = false;
    usersApi.list()
      .then((us) => { if (!cancelled) setMembers((us || []).map((u) => ({ id: u.id, name: u.name || u.email, role: u.role }))); })
      .catch(() => { if (!cancelled) setMembers([]); });
    return () => { cancelled = true; };
  }, [isManager]);

  useEffect(() => {
    if (!pipelineId) return;
    let cancelled = false;
    setBoardLoading(true);
    pipelineTechApi.board(pipelineId)
      .then((data) => { if (!cancelled) setBoard(data || { stages: [], cards: [] }); })
      .catch(() => { if (!cancelled) setBoard({ stages: [], cards: [] }); })
      .finally(() => { if (!cancelled) setBoardLoading(false); });
    return () => { cancelled = true; };
  }, [pipelineId]);

  // Live auto-update: refresh this board instantly on any status change or
  // pipeline-relevant event elsewhere in the app — no manual refresh needed.
  useEffect(() => {
    if (!pipelineId) return;
    const refresh = () => pipelineTechApi.board(pipelineId).then(setBoard).catch(() => {});
    window.addEventListener("job-status-changed", refresh);
    window.addEventListener("estimate-status-changed", refresh);
    window.addEventListener("pipeline-refresh", refresh);
    return () => {
      window.removeEventListener("job-status-changed", refresh);
      window.removeEventListener("estimate-status-changed", refresh);
      window.removeEventListener("pipeline-refresh", refresh);
    };
  }, [pipelineId]);

  // Client-side owner filter — the backend returns every card for admins,
  // so filtering here keeps the "preview by user" toggle instant.
  const visibleCards = useMemo(() => {
    const all = board.cards || [];
    if (ownerFilter === "all") return all;
    if (ownerFilter === "unassigned") return all.filter((c) => !c.assigneeId);
    return all.filter((c) => c.assigneeId === ownerFilter);
  }, [board.cards, ownerFilter]);

  const cardsByStage = useMemo(() => {
    const out = {};
    visibleCards.forEach((c) => {
      const k = c.stageId || "__none__";
      (out[k] ||= []).push(c);
    });
    return out;
  }, [visibleCards]);

  const stages = board.stages || [];

  const scrollToStage = (idx) => {
    if (!scrollerRef.current) return;
    const col = scrollerRef.current.querySelector(`[data-stage-idx="${idx}"]`);
    if (col) col.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
    setActiveStageIdx(idx);
  };

  const onScroll = () => {
    if (!scrollerRef.current) return;
    const cols = scrollerRef.current.querySelectorAll("[data-stage-idx]");
    const left = scrollerRef.current.scrollLeft;
    let closest = 0, best = Infinity;
    cols.forEach((c) => {
      const idx = Number(c.getAttribute("data-stage-idx"));
      const d = Math.abs(c.offsetLeft - left);
      if (d < best) { best = d; closest = idx; }
    });
    if (closest !== activeStageIdx) setActiveStageIdx(closest);
  };

  return (
    <div
      className="mv md:hidden min-h-screen w-full"
      style={{
        color: "#0f172a",
        paddingTop: "calc(3.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(5rem + env(safe-area-inset-bottom))",
      }}
      data-testid="mobile-pipeline"
    >
      {loading ? (
        <div className="p-10 flex items-center justify-center text-slate-400 text-sm" data-testid="mobile-pipeline-loading">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading pipelines…
        </div>
      ) : !pipelines.length ? (
        <div className="p-10 text-center text-slate-500 text-sm" data-testid="mobile-pipeline-empty">
          No pipelines available yet.
        </div>
      ) : (
        <>
          {/* Pipeline + Owner selectors */}
          <div className="mx-4 mt-3 bg-white rounded-2xl p-3 border border-slate-200">
            <label className="block text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">Pipeline</label>
            <select
              value={pipelineId}
              onChange={(e) => setPipelineId(e.target.value)}
              data-testid="mobile-pipeline-picker"
              className="w-full h-10 px-2 rounded-md border border-slate-300 bg-white text-[14px] font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            >
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            {isManager && (
              <>
                <label className="block text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mt-3 mb-1 inline-flex items-center gap-1">
                  <UsersIcon className="h-3 w-3" /> View pipeline by
                </label>
                <select
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  data-testid="mobile-pipeline-owner-filter"
                  className="w-full h-10 px-2 rounded-md border border-slate-300 bg-white text-[14px] font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                >
                  <option value="all">All owners</option>
                  <option value="unassigned">Unassigned</option>
                  {[
                    ...(user?.id ? [{ id: user.id, label: `${user.name || user.email} (me)` }] : []),
                    ...members
                      .filter((m) => m.id !== user?.id)
                      .map((m) => ({ id: m.id, label: `${m.name} · ${(m.role || "").replace(/_/g, " ")}` })),
                  ].map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </>
            )}

            <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-2">
              <span data-testid="mobile-pipeline-count">{visibleCards.length} card{visibleCards.length === 1 ? "" : "s"}</span>
              <span className="text-slate-300">·</span>
              <span>{stages.length} stage{stages.length === 1 ? "" : "s"}</span>
            </div>
          </div>

          {/* Stage nav pills */}
          {stages.length > 0 && (
            <div className="mx-4 mt-3 flex items-center gap-2">
              <button
                onClick={() => scrollToStage(Math.max(0, activeStageIdx - 1))}
                disabled={activeStageIdx <= 0}
                data-testid="mobile-pipeline-prev"
                className="h-8 w-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4 shrink-0" />
              </button>
              <div className="flex-1 overflow-x-auto no-scrollbar">
                <div className="flex gap-1.5">
                  {stages.map((s, i) => {
                    const active = i === activeStageIdx;
                    return (
                      <button
                        key={s.id}
                        onClick={() => scrollToStage(i)}
                        data-testid={`mobile-pipeline-stage-pill-${s.id}`}
                        className={`px-3 h-8 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors ${
                          active
                            ? "bg-blue-600 text-white"
                            : "bg-white border border-slate-200 text-slate-700"
                        }`}
                      >
                        {s.name}
                        <span className={`ml-1.5 ${active ? "text-white/80" : "text-slate-400"}`}>
                          {(cardsByStage[s.id] || []).length}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                onClick={() => scrollToStage(Math.min(stages.length - 1, activeStageIdx + 1))}
                disabled={activeStageIdx >= stages.length - 1}
                data-testid="mobile-pipeline-next"
                className="h-8 w-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4 shrink-0" />
              </button>
            </div>
          )}

          {/* Horizontal-swipe Kanban */}
          {boardLoading ? (
            <div className="p-10 flex items-center justify-center text-slate-400 text-sm" data-testid="mobile-pipeline-board-loading">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading board…
            </div>
          ) : stages.length === 0 ? (
            <div className="mx-4 mt-3 p-8 bg-white rounded-2xl text-center text-slate-500 text-sm border border-slate-200">
              This pipeline has no stages yet.
            </div>
          ) : (
            <div
              ref={scrollerRef}
              onScroll={onScroll}
              className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar mt-3 pb-4 gap-3 px-4"
              data-testid="mobile-pipeline-scroller"
              style={{ scrollBehavior: "smooth" }}
            >
              {stages.map((s, i) => {
                const list = cardsByStage[s.id] || [];
                return (
                  <div
                    key={s.id}
                    data-stage-idx={i}
                    data-testid={`mobile-pipeline-stage-${s.id}`}
                    className="snap-start shrink-0 w-[85%] max-w-[340px] bg-white rounded-2xl p-3 flex flex-col border border-slate-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[13.5px] font-bold text-slate-900">{s.name}</div>
                      <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                        {list.length}
                      </span>
                    </div>
                    {list.length === 0 ? (
                      <div className="py-6 text-center text-[12px] text-slate-400">No cards here</div>
                    ) : (
                      <div className="space-y-2">
                        {list.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => c.customerId && navigate(`/customers?id=${c.customerId}`)}
                            data-testid={`mobile-pipeline-card-${c.id}`}
                            className="w-full text-left bg-slate-50 rounded-xl p-3 active:bg-slate-100 border border-slate-200"
                          >
                            <div className="text-[13.5px] font-bold text-slate-900 truncate">{c.customerName || "Unknown customer"}</div>
                            {c.subtitle && <div className="text-[11.5px] text-slate-500 truncate mt-0.5">{c.subtitle}</div>}
                            <div className="mt-2 flex items-center gap-3 flex-wrap text-[11.5px] text-slate-500">
                              {c.value > 0 && (
                                <span className="inline-flex items-center gap-1 font-semibold text-emerald-700"><DollarSign className="h-3 w-3" /> {money(c.value)}</span>
                              )}
                              {c.assigneeName && (
                                <span className="inline-flex items-center gap-1 text-slate-600"><UsersIcon className="h-3 w-3" /> {c.assigneeName}</span>
                              )}
                              {c.customerId && (
                                <span className="inline-flex items-center gap-1 text-blue-600"><MapPin className="h-3 w-3" /> Open</span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
